import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import { collection, doc, getDocs, getFirestore, query, where, writeBatch, onSnapshot, serverTimestamp, connectFirestoreEmulator, setDoc, deleteDoc } from "firebase/firestore";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { BMServer, BMServerToUpdate, IP, IP_STATUS } from "../model/BMServer";


let isInitialisedFireStore = false;

const firebaseConfig = {
    apiKey: "AIzaSyCJVjr-_Y-v8eJcBFuP6dUCJTtSnfGTvqE",
    authDomain: "com-cool-vpn.firebaseapp.com",
    databaseURL: "https://com-cool-vpn-default-rtdb.firebaseio.com",
    projectId: "com-cool-vpn",
    storageBucket: "com-cool-vpn.appspot.com",
    messagingSenderId: "889974893661",
    appId: "1:889974893661:web:137ac3b26a4ef0a5b5b1b4",
    measurementId: "G-RCKTFT2H9G"
};
export const isDevMode = (window.location.hostname == 'localhost' || window.location.hostname == 'ryn-servers.wcheck.in');

export const bmServerCollectionName = isDevMode ? "bm_servers_dev" : "bm_servers"

export const app = initializeApp(firebaseConfig);


export const authentication = getAuth(app);

export const db = getFirestore()

export const provider = new GoogleAuthProvider();

export const functions = getFunctions(app, 'us-central1')

// Connect to emulator in dev mode
// NOTE: Emulator has CORS issues with callable functions
// Using production functions with Test Mode for safe testing
// if (isDevMode) {
//     console.log("🔧 DEV MODE: Connecting to Firebase Emulators");
//     connectFunctionsEmulator(functions, "127.0.0.1", 5001);
// }

// Helper to call APIs via hosting rewrite (avoids CORS)
const callApiViaRewrite = async (endpoint: string, data: any) => {
    const currentUser = authentication.currentUser;
    if (!currentUser) return Promise.reject("User not logged in");

    const token = await currentUser.getIdToken();
    try {
        const res = await fetch(endpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token
            },
            // Callable functions expect { data: ... } body
            body: JSON.stringify({ data })
        });

        let responseData;
        const isJson = res.headers.get('content-type')?.includes('application/json');

        if (isJson) {
            responseData = await res.json();
        } else {
            responseData = await res.text();
        }

        if (!res.ok) {
            const message = typeof responseData === 'object' ? (responseData.error?.message || responseData.message) : responseData;
            throw new Error(`API Error (${res.status}): ${message || res.statusText}`);
        }

        // Callable functions return { result: ... }
        return { data: isJson ? responseData.result : responseData };
    } catch (e) {
        console.error(`API Call failed for ${endpoint}:`, e);
        throw e;
    }
}

export const proxyNetdataApi = (ip: string) => {
    return callApiViaRewrite("/api-proxyNetdata", { ip });
};

export const updateServerApi = (data: any) => {
    return callApiViaRewrite("/api-updateServerApiV2", data);
};

// Not suffixed with _dev: /api-currentExpectedTraffic always reads this collection
export const extraTrafficCollectionName = "extra_traffic_servers"

export type ExtraTrafficApp = "spl" | "karak"

export interface ExtraTrafficServer {
    host: string
    app: ExtraTrafficApp
    addedBy?: string
}

export function normalizeTrafficHost(input: string): string {
    return input.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/:443$/, "")
}

export function listenExtraTrafficServers(onChange: (servers: ExtraTrafficServer[]) => void) {
    return onSnapshot(query(collection(db, extraTrafficCollectionName), where("enabled", "==", true)), snapshot => {
        onChange(snapshot.docs.map(d => ({
            host: String(d.data().host),
            app: d.data().app === "karak" ? "karak" : "spl",
            addedBy: d.data().addedBy
        })))
    })
}

export function addExtraTrafficServer(host: string, app: ExtraTrafficApp) {
    return setDoc(doc(db, extraTrafficCollectionName, host), {
        host: host,
        app: app,
        enabled: true,
        addedBy: authentication.currentUser?.email || "",
        createdAt: serverTimestamp()
    })
}

export function removeExtraTrafficServer(host: string) {
    return deleteDoc(doc(db, extraTrafficCollectionName, host))
}

export function initFireStoreEvents(listenerFn: () => void) {
    if (isInitialisedFireStore) return;
    isInitialisedFireStore = true;
    const q = query(collection(db, "" + bmServerCollectionName + ""));
    getDocs(q).then(snapshot => {
        for (const currentDoc of snapshot.docs) {
            onSnapshot(doc(db, bmServerCollectionName, currentDoc.id), { includeMetadataChanges: true }, (updatedSnapshot) => {
                console.log("Fire Store data update ")
                listenerFn()
            })
        }
    });
}

export async function updateIpsForBMServer(server: BMServer) {
    let batch = writeBatch(db)
    server.ips.forEach(ip => {
        const ipRef = doc(db, "" + bmServerCollectionName + "", server.serverName + "/ips/" + ip.ip);
        batch.set(ipRef, {
            status: ip.status,
            secondary_ip: (ip.secondaryIp || "").toString().trim().replace(/:\d+$/, ""),
            dual_https_port: Number(ip.dualHttpsPort) > 0 ? Number(ip.dualHttpsPort) : 443,
            dual_https_enabled: !!ip.dualHttpsEnabled
        }, { merge: true });
    })

    const serverRef = doc(db, bmServerCollectionName, server.serverName);
    // Do not touch updatedBy/updatedOn here — those change only on Update IP
    let updateData: any = {
        dual_https_only_pairs: (server.dualHttpsOnlyPairs || []).map(p => ({
            id: p.id,
            primary_ip: (p.primaryIp || "").toString().trim().replace(/:\d+$/, ""),
            secondary_ip: (p.secondaryIp || "").toString().trim().replace(/:\d+$/, ""),
            port: Number(p.port) > 0 ? Number(p.port) : 443,
            enabled: !!p.enabled
        })).filter(p => p.primary_ip && p.secondary_ip)
    };
    if (server.serverType) updateData.server_type = server.serverType;
    if (server.app) updateData.app = server.app;
    batch.set(serverRef, updateData, { merge: true });

    await batch.commit()
    return true
}
export function fetchServerInfo(onSuccess: (res: Map<string, BMServer>) => void, onFail: () => void) {
    let servers = new Map<string, BMServer>();
    const q = query(collection(db, "" + bmServerCollectionName + ""), where("is_active", "==", true));
    getDocs(q).then(snapshot => {
        let count = snapshot.size;
        let found = 0
        for (const docSnap of snapshot.docs) {
            const rawPairs = docSnap.get("dual_https_only_pairs") || [];
            fetchIpInfo({
                currentIp: docSnap.get("current_ip"),
                ips: [],
                link: docSnap.get("current_ip"),
                serverName: docSnap.id,
                traffic: 0,
                hasBadTraffic: false,
                updatedBy: docSnap.get("updatedBy"),
                updatedOn: docSnap.get("updatedOn"),
                serverType: docSnap.get("server_type") || "general",
                app: docSnap.get("app") || "SPL",
                isPremium: docSnap.get("is_premium") || false,
                manualRegion: docSnap.get("manual_region") || "",
                dualHttpsOnlyPairs: (Array.isArray(rawPairs) ? rawPairs : []).map((p: any, idx: number) => ({
                    id: p.id || ("pair_" + idx),
                    primaryIp: p.primary_ip || p.primaryIp || "",
                    secondaryIp: p.secondary_ip || p.secondaryIp || "",
                    port: p.port || 443,
                    enabled: !!p.enabled
                }))
            }).then(server => {
                servers.set(docSnap.id, server)
            }).finally(() => {
                found++;
                if (count == found) {
                    onSuccess(servers)
                }
            })
        }
        return servers
    }).catch(e => {
        onFail()
    });
}
export async function fetchIpInfo(server: BMServer) {
    server.ips = []
    let q2 = query(collection(db, "" + bmServerCollectionName + "/" + server.serverName + "/ips"), where("status", "!=", IP_STATUS.DISABLED));
    let snapshot2 = await getDocs(q2)
    for (const doc2 of snapshot2.docs) {
        server.ips.push({
            ip: doc2.id,
            status: doc2.get("status"),
            secondaryIp: doc2.get("secondary_ip") || "",
            dualHttpsPort: doc2.get("dual_https_port") || 443,
            dualHttpsEnabled: !!doc2.get("dual_https_enabled")
        })
    }
    return server
}
export async function updateBMServer(servers: BMServerToUpdate[]) {
    let batch = writeBatch(db)

    // Categorize IPs
    let generalIps: string[] = []
    let gamingIps: string[] = []
    let streamingIps: string[] = []

    servers.forEach(n => {
        let ipRef = doc(db, "" + bmServerCollectionName + "", n.serverName);
        let bmServerUpdate = JSON.parse("{}");
        bmServerUpdate.current_ip = n.newIp;
        if (n.currentIp != n.newIp) {
            bmServerUpdate.updatedBy = authentication.currentUser?.displayName
            bmServerUpdate.updatedOn = new Date().getTime()
        }
        // Ensure server_type and app are preserved or updated
        bmServerUpdate.server_type = n.serverType;
        bmServerUpdate.app = n.app;
        bmServerUpdate.is_premium = n.isPremium;
        if (n.manualRegion && n.manualRegion.trim() !== "") {
            bmServerUpdate.manual_region = n.manualRegion.trim();
        } else {
            // Optional: delete manual_region field if empty? 
            // batch.update(ipRef, { manual_region: deleteField() }); // Requires import
            // For now just setting empty string or checking logic server-side.
            // But simpler to just set it.
            bmServerUpdate.manual_region = "";
        }

        batch.set(ipRef, bmServerUpdate, { merge: true });
        // Persist dual-HTTPS-only pair selection (never written into ips)
        if (n.dualHttpsOnlyPairs) {
            batch.set(ipRef, {
                dual_https_only_pairs: n.dualHttpsOnlyPairs.map(p => ({
                    id: p.id,
                    primary_ip: (p.primaryIp || "").toString().trim().replace(/:\d+$/, ""),
                    secondary_ip: (p.secondaryIp || "").toString().trim().replace(/:\d+$/, ""),
                    port: Number(p.port) > 0 ? Number(p.port) : 443,
                    enabled: !!p.enabled
                })).filter(p => p.primary_ip && p.secondary_ip)
            }, { merge: true });
        }
        if (n.newIp != "") {
            // Persist dual_https enabled flag on the active primary IP doc
            const activeIpRef = doc(db, "" + bmServerCollectionName + "", n.serverName + "/ips/" + n.newIp.trim().replace(":443", ""));
            batch.set(activeIpRef, { dual_https_enabled: !!n.dualHttpsEnabled }, { merge: true });
        }
        if (n.currentIp != n.newIp && n.currentIp != "") {
            let ipRef2 = doc(db, "" + bmServerCollectionName + "", n.serverName + "/ips/" + n.currentIp);
            let ip_update = JSON.parse("{}")
            ip_update["note"] = n.reason.toString()
            if (n.reason == IP_STATUS.BLOCKED) {
                ip_update["status"] = IP_STATUS.BLOCKED.toString()
            }
            batch.set(ipRef2, ip_update, { merge: true });
        }

        if (n.newIp != "") {
            let formattedIp = n.newIp.trim().replace(":443", "") + ":443"
            if (n.serverType === "gaming") {
                gamingIps.push(formattedIp)
            } else if (n.serverType === "streaming") {
                streamingIps.push(formattedIp)
            } else {
                generalIps.push(formattedIp)
            }
        }
    })
    await batch.commit()

    // Return structured object instead of just string
    return {
        newIp: generalIps.join(","),
        gamingNewIp: gamingIps.join(","),
        streamingNewIp: streamingIps.join(",")
    }
}

export async function addBMServer(serverName: string, ip: string, serverType: string, app: string) {
    const batch = writeBatch(db);
    const serverRef = doc(db, bmServerCollectionName, serverName);

    // 1. Create Server Document
    batch.set(serverRef, {
        current_ip: ip || "", // Set initial IP as current (can be empty)
        server_type: serverType,
        app: app,
        is_active: true,
        updatedBy: authentication.currentUser?.email || "admin",
        updatedOn: Date.now(),
    });

    // 2. Create IP Sub-document ONLY if IP is provided
    if (ip && ip.trim() !== "") {
        const ipRef = doc(db, `${bmServerCollectionName}/${serverName}/ips`, ip);
        batch.set(ipRef, {
            status: IP_STATUS.ACTIVE,
            addedOn: Date.now(),
            addedBy: authentication.currentUser?.email || "admin"
        });
    }

    await batch.commit();
    return true;
}